import _ from 'lodash';
import qs from 'query-string';
import moment from 'moment';
import {clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';

import {BASE_URL} from '../constants/config';
import {
  LEADING_JOIN_TIME,
  TRAILING_MATCH_TIMES,
  InterviewTypes,
  InterviewModes,
} from '../../../lib/constants/duet';
import {AIOrgAllowList, JobReferralOrgDenyList, OrgSignupCaptureDomains} from '../../../lib/constants/org';
import {SocialSSOProviders} from '../../../lib/constants/sso';
import {PERSONA_CALL_FLOW_LINK} from '../../../lib/constants/persona';

export {isFinanceUser} from '../../../lib/utils';

export const absoluteURLMatcher = new RegExp('^(?:[a-z]+:)?//', 'i');
export const dangerousURLMatcher = new RegExp('^javascript:', 'i');

export const isAbsoluteURL = (str) => absoluteURLMatcher.test(str);
export const isDangerousURL = (str) => dangerousURLMatcher.test(str);
export const isExternalURL = (str) => isAbsoluteURL(str) && !str.startsWith(BASE_URL);
export const isRelativeURL = (str) => (
  typeof str === 'string'
  && !isAbsoluteURL(str)
  && !isDangerousURL(str)
);

export const getPersonaCallHref = ({src, uuid, interviewId}) => {
  const params = new URLSearchParams();
  if (src) params.set('src', src);
  if (uuid) params.set('uuid', uuid);
  if (interviewId) params.set('interview', interviewId);
  const query = params.toString();
  return query ? `${PERSONA_CALL_FLOW_LINK}?${query}` : PERSONA_CALL_FLOW_LINK;
};

export const createURLWithParams = (root, params, options = {arrayFormat: 'index'}) => (
  qs.stringifyUrl({url: root, query: _.omitBy(params, _.isNil)}, options)
);

export const getReferrer = () => (typeof document !== 'undefined' ? document.referrer : null);

export const getFirstValue = (val) => {
  if (!val) return null;
  return Array.isArray(val) ? val[0] : val;
};

export const tagsToQuestionListTitle = (tags, params, succinct = false) => {
  if (!tags || !params) return '';

  const {
    company, role, type, sortBy, filterBy, difficulty,
  } = params;
  let title = '';

  // TODO: Handle multiple companies, roles, categories better
  const companyTag = _.find(tags.company, ['id', getFirstValue(company)]);
  const categoryTag = _.find(tags.type, ['id', getFirstValue(type)]);
  const roleTag = _.find(tags.role, ['id', getFirstValue(role)]);

  const sortByMap = {newest: 'Recent', 'most-popular': 'Top', hot: ''}; // "Hot" doesn't have the right ring to it
  const validDifficulties = ['Hard', 'Medium', 'Easy'];
  const sanitizedDifficulty = _.includes(validDifficulties, difficulty) ? difficulty : '';

  if ((companyTag || categoryTag || roleTag) && !succinct) {
    title += sortByMap[sortBy] || '';
  }

  if (companyTag) title += ` ${companyTag.name}`;

  if (roleTag && (!categoryTag || !companyTag)) title += ` ${roleTag.name}`;

  if (categoryTag) {
    // special cases:
    // Do not add ml category tag if role is already ml-engineer
    // If there are multiple categories set, do not add category name as to not confuse users
    if (!(categoryTag.id === 'machine-learning' && roleTag?.id === 'ml-engineer')
        && !(Array.isArray(type) && type.length > 1)) {
      title += ` ${categoryTag.name}`;
    }
  }

  // only mention difficulty level of coding question when no better title
  if (!title && filterBy === 'codeEditor') {
    title = `${sanitizedDifficulty ? `${sanitizedDifficulty} ` : ''}Coding`;
  }

  if (!succinct) title += ' Interview';

  return `${title} Questions`;
};

export const tagsToQuestionListDescription = (tags, params, numResults = 0) => {
  if (!tags || !params) return '';

  const {company, role, type} = params;
  const tagArray = _.compact([
    _.find(tags.company, ['id', company]),
    _.find(tags.type, ['id', type]),
    _.find(tags.role, ['id', role]),
  ]).map((tag) => tag.name);

  return `Review this list of ${numResults > 4 ? numResults.toLocaleString() : ''} ${tagArray.join(' ')} interview questions and answers verified by hiring managers and candidates.`;
};

/**
 * Takes an array of role tags and an array of role category
 * tags and groups roles by their parent role category. Output
 * can then be passed as the options to our DropdownInput component
*/
export const roleToRoleCategoryOptions = (roles, roleCategories) => {
  const roleOptions = (roles || [])
    .map((role) => ({...role, label: role.name, value: role.id}));
  const rolesByCategory = _.groupBy(roleOptions, 'parent_tag');
  return _(roleCategories || [])
    .filter((category) => rolesByCategory[category.id])
    .map((category) => ({label: category.name, options: rolesByCategory[category.id]}))
    .value();
};

// Maps a role tag to the set of tags it should encompass when filtering.
// e.g. selecting "swe" also surfaces frontend/backend/forward-deployed guides.
const roleTagIncludes = {
  swe: ['swe', 'frontend-engineer', 'backend-engineer', 'forward-deployed-engineer'],
};

// Returns the role tag(s) to filter by as an array. Grouped roles (e.g. swe)
// expand to all their constituent tags; ungrouped roles return a single-element array.
export const expandRole = (role) => (role ? (roleTagIncludes[role] ?? [role]) : []);

/**
 * Formats a tag such that it can be passed to options in DropdownInput or MultiSelect
 */
export const tagToOption = (tag) => ({value: tag.id, label: tag.name});

export const getEnglishTime = (timestamp) => {
  const parsed = moment(timestamp);
  const diff = (moment() - parsed) / 86400000; // 1 Day diff

  if (diff < 0.5) {
    return parsed.fromNow();
  }
  if (diff < 6) {
    return parsed.calendar();
  }
  return parsed.format('MMMM D, YYYY');
};

export const timeAgo = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  const intervals = [
    {label: 'year', seconds: 31536000},
    {label: 'month', seconds: 2592000},
    {label: 'week', seconds: 604800},
    {label: 'day', seconds: 86400},
    {label: 'hour', seconds: 3600},
  ];
  const match = intervals.find((interval) => Math.floor(seconds / interval.seconds) >= 1);
  if (match) {
    const count = Math.floor(seconds / match.seconds);
    return `${count} ${match.label}${count > 1 ? 's' : ''} ago`;
  }
  return 'Just now';
};

// If an image is hosted on Contentful, resize it to fit the given box and serve
// it as WebP. Non-Contentful URLs (e.g. YouTube thumbnails) pass through untouched.
export const resizeContentfulImage = (imageUrl, width, height) => {
  if (!imageUrl?.includes('ctfassets.net')) return imageUrl;

  const absoluteUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
  const url = new URL(absoluteUrl);
  url.searchParams.set('w', width);
  url.searchParams.set('h', height);
  url.searchParams.set('fm', 'webp');
  url.searchParams.set('q', 75);
  return url.toString();
};

// YouTube's maxresdefault.jpg is 1280x720 and true 16:9, but YouTube only
// generates it for videos uploaded at 720p or above. Pair it with
// onVideoThumbnailError so the img falls back to hqdefault.jpg (480x360,
// exists for every video) if it 404s in rare instances.
export const getVideoThumbnailURL = (videoID) => (
  `https://img.youtube.com/vi/${videoID}/maxresdefault.jpg`
);

// onError handler for any <img> that may be showing a YouTube thumbnail.
// No-op for non-YouTube URLs (e.g. Contentful images).
export const onVideoThumbnailError = (e) => {
  const {src} = e.target;
  if (src.includes('/maxresdefault.jpg')) {
    e.target.src = src.replace('/maxresdefault.jpg', '/hqdefault.jpg');
  }
};

const getCourseThumbnail = (course) => {
  if (!course) return null;
  return course.trailer
    ? getVideoThumbnailURL(course.trailer)
    : course.thumbnail;
};

export const getResizedCourseThumbnail = (course, width, height) => (
  resizeContentfulImage(getCourseThumbnail(course), width, height)
);

const getLessonThumbnail = (lesson, fallbackToCourse = true) => {
  if (!lesson) return null;
  if (lesson.image) return lesson.image;
  if (lesson.thumbnail) return lesson.thumbnail;
  if (lesson.video) return getVideoThumbnailURL(lesson.video);
  // Fall back on course thumbnail
  if (fallbackToCourse) return getCourseThumbnail(lesson.course);
  return null;
};

export const getResizedLessonThumbnail = (lesson, width, height, fallbackToCourse = true) => (
  resizeContentfulImage(getLessonThumbnail(lesson, fallbackToCourse), width, height)
);

/**
 * Returns true if an input is a valid email and false otherwise
 * @param {string} email - inputted string
 */
export const emailRegex = (email) => {
  const regex = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return regex.test(email);
};

/**
 * Simple utility that returns an absolute URL
 */
export const makeAbsoluteURL = (url) => (
  url.includes('://') || url.startsWith('//') ? url : `//${url}`
);

/**
 * Extracts error from error object if it is user friendly
 * message or will use the default message otherwise
 */
export const extractAPIError = (err = {}, defaultMessage = 'There was an unexpected error, please try again later') => {
  if (err.status < 500 && err.message) {
    return err.message;
  }
  return defaultMessage;
};

const addClass = () => {
  document.body.classList.add('using-mouse');
};

const removeClass = () => {
  document.body.classList.remove('using-mouse');
};

/**
 * Let the document know when a mouse is being used, enabling :focus and
 * :focus-within styles only when keyboard is used
 */
export const addMouseListener = () => {
  document.body.addEventListener('mousedown', addClass);
  document.body.addEventListener('keydown', removeClass);
  document.body.classList.add('using-mouse');
};

/**
 * Cleanup for addMouseListener
 */
export const removeMouseListener = () => {
  document.body.removeEventListener('mousedown', addClass);
  document.body.removeEventListener('keydown', removeClass);
};

/**
 * A11y helper for handling 'Enter' press on non-interactive elements
 */
export const onPressEnter = (cb) => (e) => {
  if (e.key === 'Enter') cb(e);
};

/**
 * Heuristic for determining e.g. whether or not to display first visit messaging
 * to a user
 * @param {object} currentUser
 */
export const isUsersFirstDay = (currentUser) => {
  if (!currentUser || !currentUser.created_at) return false;
  const oneDayInMs = 86400000;
  return (Date.now() - Date.parse(currentUser.created_at)) < oneDayInMs;
};

/**
 * @param {object} currentUser
 */
export const hasSeenInitialOnboarding = (currentUser) => {
  if (!currentUser?.profile) return false;
  return currentUser.profile.data.skippedOnboarding || currentUser.profile.data.finishedOnboarding;
};

/**
 * @param {object} currentUser
 */
export const shouldShowUniversityOnboarding = (currentUser) => currentUser?.org?.type === 'university'
&& !currentUser?.universityProfile
&& !currentUser?.isOrgAdmin;

// The capture-list university org matching an email's domain, or null
export const getOrgForEmailDomain = (email) => {
  const domain = String(email || '').trim().split('@')[1]?.toLowerCase();
  return OrgSignupCaptureDomains[domain] || null;
};

// The org to offer during onboarding: capture-domain email, personal account,
// and not already an org member or admin (admins often can't use SSO)
export const getOrgSignupCapture = (currentUser) => {
  if (!currentUser?.email || currentUser.org || currentUser.isOrgAdmin) return null;
  const sso = currentUser.sso_provider;
  if (sso && !SocialSSOProviders.includes(sso)) return null;
  return getOrgForEmailDomain(currentUser.email);
};

/**
 * @param {object} currentUser
 *
 */
export const shouldShowOnboardingVideo = (currentUser) => {
  const seenVideo = currentUser?.data?.has_seen_onboarding_video;
  return !seenVideo && isUsersFirstDay(currentUser);
};

/**
 * Returns the monthly price string based on the currency symbol and the annual plan
 * @param {string} currencySymbol currency.symbol
 * @param {number} price price of product in cents
 * @param {number} months number of months to divide by, default 12
 */
export const getMonthlyPriceStr = (currencySymbol, price, months = 12) => {
  const monthlyPrice = Math.floor(price / 100 / months);
  return `${currencySymbol}${monthlyPrice}`;
};

export const shouldShowOnboarding = (user, pathname) => {
  if (!user || user.needs_password) return false;

  // Paths that shouldn't be interrupted with onboarding
  if (pathname.includes('/onboarding')
    || pathname.includes('coaching/onboarding')
    || pathname.includes('/coaching/booking')
    || pathname.includes('/questions/contribute')) {
    return false;
  }

  // University onboarding is mandatory, so applies even if user finished or skipped generic flow
  if (shouldShowUniversityOnboarding(user)) return true;

  return !user.profile?.data?.skippedOnboarding && !user.profile?.data?.finishedOnboarding;
};

/**
 * Wraps the FileReader Web API to make it async/await friendly
 * @param {File} file file object to encode as a base64 string
 */
export const encodeFileAsync = (file) => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = reject;

    reader.readAsDataURL(file);
  })
);

/**
 * Pretty print a number into tens, hundreds, thousands, millions,
 * or billions. Always rounds down.
 * @param {number} num
 * @param {boolean} floorUnderThousand when true, sub-1000 values floor to one
 *   significant figure (47 -> 40, 437 -> 400) instead of being returned exactly.
 */
export const formatLargeNum = (num, floorUnderThousand = false) => {
  if (!Number.isInteger(num)) return '';

  if (num < 1000) {
    if (!floorUnderThousand || num < 10) return `${num}`;

    const magnitude = 10 ** Math.floor(Math.log10(num));
    return `${Math.floor(num / magnitude) * magnitude}`;
  }

  const abbrevs = {3: 'k', 6: 'm', 9: 'b'};
  let truncated = Math.floor(num / 100) / 10; // Keep one decimal place
  let tenToThe = 3;
  while (truncated) {
    if (truncated < 1000) {
      return truncated >= 100
        ? `${Math.floor(truncated)}${abbrevs[tenToThe]}`
        : `${truncated}${abbrevs[tenToThe]}`;
    }

    tenToThe += 3;
    truncated = Math.floor(truncated / 100) / 10; // Keep one decimal place
  }
  return '';
};

/**
 * Formats a duration in minutes into a human-readable string.
 * @param {number} time - Duration in minutes.
 * @param {number} [threshold=90] - Threshold (in minutes) to switch from minutes to hours.
 * @param {boolean} [short=false] - Whether to use short units (e.g., "min" / "hr").
 * @returns {string|null} Formatted duration string.
 */
export const formatMinutes = (time, threshold = 90, short = false) => {
  if (!time) return null;

  const useHours = time >= threshold;
  const rounded = useHours ? Math.round(time / 60) : Math.round(time);

  let unit;

  if (short) {
    unit = useHours ? 'hr' : 'min';
  } else if (useHours) {
    unit = rounded === 1 ? 'hour' : 'hours';
  } else {
    unit = rounded === 1 ? 'minute' : 'minutes';
  }

  return `${rounded} ${unit}`;
};

/**
 * Returns an object of style properties that limit the number of lines
 * of text displayed by an element.
 * @param {number} num number of lines to show
 */
export const clampLines = (num) => ({
  display: '-webkit-box',
  WebkitLineClamp: `${num}`,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

export const startsWithVowel = (str = '') => /^[aeiou]/.test(str.toLowerCase());

export const sleep = async (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const shouldHideCoaching = (user) => (
  user?.org?.type === 'university' || user?.data?.hideCoaching
);

/**
 * Returns the text content of a React node. Stolen from:
 * https://stackoverflow.com/a/60564620/3681279
 * @param {React.ReactNode} node
 * @returns {string}
 */
export const getNodeText = (node) => {
  if (node == null) return '';

  switch (typeof node) {
    case 'string':
    case 'number':
      return node.toString();

    case 'boolean':
      return '';

    case 'object': {
      if (node instanceof Array) { return node.map(getNodeText).join(''); }

      if ('props' in node) { return getNodeText(node.props.children); }
    } // eslint-ignore-line no-fallthrough

    default:
      console.warn('Unresolved `node` of type:', typeof node, node);
      return '';
  }
};

/**
 * New if lesson has not been read and it has been published less than 2 weeks ago
 * @param {lesson} lesson
 * @returns {boolean}
 */
export const isNewLesson = (lesson) => {
  if (!lesson?.createdAt) return false;
  if (lesson.comingSoon) return false;
  const twoWeeksAgo = moment().subtract(2, 'weeks');
  const lessonCreatedAt = moment(lesson.createdAt);
  return !lesson.markedAsComplete && lessonCreatedAt.isAfter(twoWeeksAgo);
};

/**
 * New if course has been published less than 2 weeks ago
 * @param {course} course
 * @returns {boolean}
 */
export const isNewCourse = (course) => {
  if (!course?.createdAt) return false;
  const twoWeeksAgo = moment().subtract(2, 'weeks');
  const courseCreatedAt = moment(course.createdAt);
  return courseCreatedAt.isAfter(twoWeeksAgo);
};

/**
 * Doesn't support times greater than 100 hours
 * @param {number} t time in seconds
 * @returns {string} formatted time in HH:MM:SS
 */
export const formatTime = (t) => {
  const hours = Math.floor(t / 3600);
  const minutes = Math.floor((t % 3600) / 60);
  const seconds = t % 60;

  // Format each part to ensure it has two digits
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

/**
 * Returns the next match time object. Necessary because first element
 * of nextMatchTimes might be in past once tab is open for awhile.
 * @param {array} matchTimes Array of matchtime objects, as returned by getNextMatchTimes
 * @returns {object} matchTime object
*/
export const getNextMatchTime = (matchTimes) => _.find(matchTimes, (matchTimeObj) => {
  const matchTime = moment(matchTimeObj.matchTime);
  return moment().isBefore(matchTime);
});

/**
 * @param {string} sessionTime ISO 8601 string of session time
 * @returns {boolean}
 */
export const isBeforeLastMatchingOfSession = (sessionTime) => sessionTime
  && moment().isBefore(moment(sessionTime).add(_.last(TRAILING_MATCH_TIMES), 'seconds'));

/**
 * @param {string} sessionTime ISO 8601 string of session time
 * @returns {boolean}
 */
export const isAfterJoinTimeStartedForSession = (sessionTime) => sessionTime
  && moment(sessionTime).diff(moment(), 'seconds') <= LEADING_JOIN_TIME;

/**
 * @param {string} sessionTime ISO 8601 string of session time
 * @returns {boolean}
*/
export const isWithinJoinTimeForSession = (sessionTime) => (
  isAfterJoinTimeStartedForSession(sessionTime) && isBeforeLastMatchingOfSession(sessionTime)
);

/**
 * Currently defined as [8 AM, 12 AM)
 * @param {string} sessionTime ISO 8601 string of session time
 * @returns {boolean}
*/
export const isDuringUsersWakingHours = (sessionTime) => {
  const localHour = moment(sessionTime).local().hour();
  return localHour >= 8 && localHour < 24;
};

// De-duplicated, sorted, still-joinable session times for an interview type.
export const getUpcomingTimesForType = (typeKey, nextMatchTimes) => {
  const timeWindowFilter = typeKey === InterviewTypes.Algorithms.key
    ? isDuringUsersWakingHours
    : () => true;

  return _((nextMatchTimes && nextMatchTimes[typeKey]) || [])
    .map('sessionTime')
    .filter(isBeforeLastMatchingOfSession)
    .filter(timeWindowFilter)
    .uniq()
    .sort()
    .value();
};

/**
 * Takes a MediaStreamTrack object from the Media Streams API
 * and returns relevant properties, so we can store for later use
 * @param {MediaStreamTrack} track
 * @returns {object}
*/
export const formatMediaStreamTrack = (track) => {
  if (!(track instanceof MediaStreamTrack)) {
    throw new Error('The provided input is not a MediaStreamTrack.');
  }

  return {
    id: track.id,
    kind: track.kind,
    label: track.label,
    enabled: track.enabled,
    muted: track.muted,
    readyState: track.readyState,
    settings: track.getSettings(),
    capabilities: track.getCapabilities?.(), // Not supported in Firefox
    constraints: track.getConstraints(),
  };
};

/**
 * Takes a NetworkInformation object from the Network Information API
 * and returns relevant properties, so we can store for later use.
 * Basically only works on Chromium browsers.
 * @param {NetworkInformation} connection
 * @returns {object}
*/
export const formatNetworkConnection = (connection) => {
  if (!connection) return connection;

  if (!(connection instanceof NetworkInformation)) {
    throw new Error('The provided input is not a NetworkInformation.');
  }

  return {
    downlink: connection.downlink,
    effectiveType: connection.effectiveType,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
};

/**
 * Get display time for a past session. Falls back on createdAt
 * for backwards compatibility, with a rough heuristic for
 * rounding to session time
 */
export const getDisplayTimeForPreviousDuetSession = (session) => {
  if (session.mode === InterviewModes.OnDemand || session.mode === InterviewModes.AI) {
    return moment(session.createdAt).format('MMM D, YYYY h:mm A');
  }

  return session.sessionTime
    ? moment(session.sessionTime).format('MMM D, YYYY h:mm A')
    : moment(session.createdAt).add(15, 'minutes').startOf('hour').format('MMM D, YYYY h:mm A');
};

/**
 * Formats a session time relative to now for display in tables, e.g.
 * "Today at 6:30 PM", "Tomorrow at 9 AM", "Yesterday at 3 PM", or
 * "Mar 5, 2024 at 11 AM" for times further out.
 * @param {string} t ISO 8601 string
 * @returns {string}
 */
export const formatRelativeSessionTime = (t) => moment(t).calendar(null, {
  sameDay: '[Today at] h:mm A',
  nextDay: '[Tomorrow at] h:mm A',
  nextWeek: 'dddd [at] h:mm A',
  lastDay: '[Yesterday at] h:mm A',
  lastWeek: '[Last] dddd [at] h:mm A',
  sameElse: 'MMM D, YYYY [at] h:mm A',
});

/**
 * Compact relative session time for carousel cards, e.g.
 * "Today 6:30 PM", "Tomorrow 9 AM", "Wed 11 AM", "Mar 5, 11 AM".
 * @param {string} t ISO 8601 string
 * @returns {string}
 */
export const formatCompactSessionTime = (t) => moment(t).calendar(null, {
  sameDay: '[Today] h:mm A',
  nextDay: '[Tomorrow] h:mm A',
  nextWeek: 'ddd h:mm A',
  sameElse: 'MMM D, h:mm A',
});

/**
 * Relative display time for a past session, accounting for the same
 * createdAt fallbacks as getDisplayTimeForPreviousDuetSession.
 * @param {object} session
 * @returns {string}
 */
export const getRelativeTimeForPreviousDuetSession = (session) => {
  if (session.mode === InterviewModes.OnDemand || session.mode === InterviewModes.AI) {
    return formatRelativeSessionTime(session.createdAt);
  }

  return session.sessionTime
    ? formatRelativeSessionTime(session.sessionTime)
    : formatRelativeSessionTime(moment(session.createdAt).add(15, 'minutes').startOf('hour'));
};

export const toDuetInterviewTypeName = (type) => InterviewTypes[type]?.name || _.startCase(type);

/**
 * Takes a Duet session and returns whether AI feedback is available
 * for that interview mode
 * @param {object} session
 * @returns {boolean}
*/
export const isInterviewModeWithoutAI = (session) => [
  InterviewModes.OnDemand,
  InterviewModes.Pramp,
].includes(session?.mode);

/**
 * Given the currentUser and a Duet session, returns whether AI
 * feedback UI elements should be shown
 * @param {object} currentUser
 * @param {object} [session]
 * @returns {boolean}
*/
export const hideAIFeedbackFeatures = (currentUser, session) => {
  const isOrgMember = Boolean(currentUser?.org);
  const isAllowListedOrg = currentUser?.org?.slug in AIOrgAllowList;
  const isOrgAdmin = currentUser?.isOrgAdmin;

  if (isOrgMember && !isAllowListedOrg && !isOrgAdmin) return true;

  if (session) {
    return !InterviewTypes[session.type]?.hasAIFeedback || isInterviewModeWithoutAI(session);
  }

  return false;
};

/**
 * Given the currentUser and a Duet session, returns whether AI
 * single player mode UI elements should be shown
 * @param {object} currentUser
 * @param {object} [session]
 * @returns {boolean}
*/
export const hideAIPracticeFeatures = (currentUser, session) => {
  const isOrgUser = Boolean(currentUser?.org);
  const isAllowListedOrg = currentUser?.org?.slug in AIOrgAllowList;

  if (isOrgUser && !isAllowListedOrg) return true;

  if (session) {
    return !InterviewTypes[session?.type]?.hasAIPractice;
  }

  return false;
};

/**
 * Given the currentUser, returns whether Chat with Ace UI elements should be
 * hidden. Gated on the org alone, not on AI entitlement.
 * @param {object} currentUser
 * @returns {boolean}
*/
export const hideAIChatFeatures = (currentUser) => {
  const isOrgUser = Boolean(currentUser?.org);
  const isAllowListedOrg = currentUser?.org?.slug in AIOrgAllowList;

  if (isOrgUser && !isAllowListedOrg) return true;

  return false;
};

/**
 * Given the currentUser, returns whether job referral UI elements should be
 * hidden
 * @param {object} currentUser
 * @returns {boolean}
*/
export const shouldHideJobReferrals = (currentUser) => Boolean(
  currentUser?.org?.slug && currentUser.org.slug in JobReferralOrgDenyList,
);

/**
 * Given an activeLanguage from the CodeEngine or SQLEngine, return
 * the language key to pass to @monaco-editor/react
 * @param {string} activeLanguage
 * @returns {boolean}
*/
export const toMonacoLanguage = (activeLanguage) => {
  if (activeLanguage === 'sqlite' || activeLanguage === 'postgres') {
    return 'sql';
  }

  return activeLanguage;
};

/**
 * Given a Code Problem slug, return the key to retrieve from
 * localStorage.
 *
 * For backwards compatibility reasons this is labeled as
 * 'draftLessonSolution', but in reality these Code Problems can be
 * encountered in many contexts (lessons, questions, duet, etc.)
 *
 * @param {string} slug Code Problem slug, usually prepended in order to namespace by context
 * @returns {string}
*/
export const getDraftKey = (slug) => `draftLessonSolution-${slug}`;

/**
 * Given a Code Problem slug, try to retrieve a draft from
 * localStorage. If multiple drafts exist, return the one with
 * the most recent timestamp.
 *
 * @param {string} slug Code Problem slug, usually prepended in order to namespace by context
 * @returns {object}
*/
export const getMostRecentDraft = (slug) => {
  if (typeof window === 'undefined') return null;

  const draftKey = getDraftKey(slug);
  const localStorageDrafts = JSON.parse(localStorage.getItem(draftKey));

  if (_.isEmpty(localStorageDrafts)) return null;

  const mostRecentDraftLanguage = _.maxBy(
    _.keys(localStorageDrafts),
    (language) => localStorageDrafts[language].time,
  );
  const mostRecentDraft = localStorageDrafts[mostRecentDraftLanguage];
  return {language: mostRecentDraftLanguage, ...mostRecentDraft};
};

/**
 * Adds no. of weekdays to a date. Does not consider public holidays
 * @param {Date | string} date - The starting date
 * @param {number} numDays - The number of business days to add
 * @returns {Date} - A JavaScript Date object representing the final computed date
 */
export const addBusinessDays = (date, numDays) => {
  const result = moment(date);
  let remainingDays = numDays;

  while (remainingDays > 0) {
    result.add(1, 'days');
    if (result.isoWeekday() <= 5) {
      remainingDays -= 1;
    }
  }

  return result.toDate();
};

/**
 * Pare down to only fields necessary for /coaching page
 * @param {*} coaches coaches from getPublicCoachingData
 * @returns cleaned coaches to be displayed on /coaching pages
 */
export const cleanCoaches = (coaches) => {
  if (!Array.isArray(coaches)) return [];

  return coaches.map((coach) => ({
    slug: coach.slug,
    Name: coach.Name,
    Title: coach.Title,
    Rating: coach.Rating,
    'Brief Bio': coach['Brief Bio'],
    'Total number of sessions completed': coach['Total number of sessions completed'],
    Services: coach.Services ?? [],
    Companies: coach.Companies ?? [],
    'Companies Worked': coach['Companies Worked'] ?? [],
    'Companies Interviewed': coach['Companies Interviewed'] ?? [],
    Role: coach.Role ?? null,
    Photo: coach.Photo?.[0].url ?? null,
    Skills: coach.Skills?.map((tag) => tag.id) ?? [],
    Industries: coach.Industries?.map((tag) => tag.id) ?? [],
    numHighlights: coach['Highlights (from Ratings from Students)']?.length || 0,
  }));
};

/**
 * Converts a company name to a db tag object
 * @param {*} name name of company (e.g. "Meta", "TikTok")
 * @param {*} tagMap map of tag.id to tag object
 * @returns tag object or null if not found
 */
export const companyNameToTag = (name, tagMap) => {
  const specialCase = {
    meta: 'facebook', // tag.id for Meta is still 'facebook'
  };

  // Example: TikTok -> tik-tok or tiktok (matches db tag.id)
  const kebabCased = _.kebabCase(name.trim());
  const mapped = specialCase[kebabCased] || kebabCased;
  const tag = tagMap[mapped] || tagMap[kebabCased.replace(/-/g, '')];

  return tag || null;
};

export function cx(...inputs) {
  return twMerge(clsx(inputs));
}
